/**
 * `@raad/geo/basemap` — the ONE framework-free brain for the Google Map Tiles
 * basemap (slice 51).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS FRAMEWORK-FREE, AND WHY THE KEY IS AN ARGUMENT.
 * ─────────────────────────────────────────────────────────────────────────────
 * `web/` and `admin/` both offer the same three basemaps (Google Map, Google
 * Satellite, Street map) with the same fallback rules, the same session cache and
 * the same expiry maths. Two copies of a session-refresh boundary or a fallback
 * latch drift, and a drifted latch is a retry loop hammering a metered Somali
 * link. So the decisions live here ONCE, as pure functions and an injectable
 * cache — no React, no maplibre, no DOM, no `import.meta.env`. The Maps key is
 * passed IN by each app's thin adapter; this module never reads an env var, so it
 * cannot leak one and can be unit-tested with a fake key and a fake `fetch`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * OBSERVED GOOGLE FACTS — measured 12 Sep 2026 by the slice-51 gating probe
 * (`.devscratch/51-probe.mjs`, dev key, Referer http://localhost:5173/, region
 * SO, language en). Every constant and fallback rule below is derived from these,
 * NOT from a reading of Google's docs (CLAUDE.md: "never mock a dependency's
 * behaviour you have not observed").
 * ─────────────────────────────────────────────────────────────────────────────
 *   createSession roadmap   → HTTP 200; keys {session, expiry, tileWidth,
 *                             imageFormat, tileHeight}; tileWidth=tileHeight=256;
 *                             imageFormat "png"; session length 86; expiry is a
 *                             UNIX-SECONDS integer (1790373379 → 2026-09-25,
 *                             ~13 days out — sessions last ~2 weeks; createSession
 *                             is unbilled, so refreshing early is free).
 *   createSession satellite → HTTP 200; imageFormat "jpeg"; session length 88;
 *                             layerTypes:["layerRoadmap"] ACCEPTED — the saved
 *                             satellite tile showed road labels ("Villa Somalia",
 *                             "Beerta Nabadda", "HAMAR WEYNE") over imagery.
 *   2dtiles roadmap Somalia z5/19/15 → 200 image/png 19565 bytes.
 *   2dtiles OVER-MAX-ZOOM z22 open ocean → 200 image/png 178 bytes — a valid tiny
 *     tile, NOT a 404 and NOT an error. This is why the non-403/429 flip rule
 *     below counts ONLY genuine error statuses: Google does not 404 an
 *     over-zoomed tile, so over-zoom can never contribute to the error window.
 *   viewport Mogadishu → 200 {copyright:"Map data ©2026 Google", maxZoomRects:100}
 *   viewport Hargeisa  → 200 {copyright:"Map data ©2026",        maxZoomRects:62}
 *     — copyright DOES vary by area, so it is refreshed on pan, not read once.
 */

/** The three basemaps a switcher offers. `openfreemap` is the existing vector
 *  street map (OpenFreeMap Liberty); the two `google-*` are the Tiles API. */
export type BasemapChoice = 'google-roadmap' | 'google-satellite' | 'openfreemap';

/** The Google session mapTypes we create sessions for. One session per mapType,
 *  cached and shared across every surface in an app. */
export type GoogleMapType = 'roadmap' | 'satellite';

/** localStorage key for the persisted basemap choice — one string, both apps. */
export const BASEMAP_STORAGE_KEY = 'raad.basemap';

/** The Tiles API host. Session create + 2dtiles + viewport all live here. */
const TILES_HOST = 'https://tile.googleapis.com';

/** How long before a session's `expiry` we proactively refresh it. Sessions last
 *  ~2 weeks and createSession is unbilled (observed), so a one-day margin removes
 *  any chance of a tile request racing an expiring session for zero cost. */
const SESSION_REFRESH_MARGIN_SEC = 24 * 60 * 60;

/** The non-403/429 tile-error flip rule: N errors inside WINDOW_MS flips to the
 *  fallback. A single transient edge error (a lone 5xx, a dropped tile) must NOT
 *  strand a user on the plain map, but a persistently failing Google source must
 *  fall back without a human watching. Over-zoom is a 200 (observed) so it never
 *  enters this window. */
const ERROR_FLIP_THRESHOLD = 3;
const ERROR_FLIP_WINDOW_MS = 10_000;

/** The maps a Google session response describes (the fields we consume). */
export interface GoogleSession {
  mapType: GoogleMapType;
  session: string;
  /** UNIX SECONDS (observed integer). Compare against `Date.now()/1000`. */
  expirySec: number;
  tileWidth: number;
  tileHeight: number;
  imageFormat: string;
}

export interface ViewportInfo {
  copyright: string;
  maxZoomRects: number;
}

/** Injected transport, so the cache and the reducer are testable with a fake and
 *  the module pulls in no DOM/fetch global of its own. */
export type FetchLike = (url: string, init?: {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
}) => Promise<{
  ok: boolean;
  status: number;
  json: () => Promise<unknown>;
}>;

/**
 * Create a Google tiles session for one mapType. UNBILLED (observed), so callers
 * may refresh freely. `region`/`language` are fixed to the product's ("SO"/"en",
 * both accepted by the probe). Throws on a non-200 or a malformed body — the
 * caller (the app adapter) turns a throw into the `sessionFailure` fallback.
 */
export async function createSession(
  mapType: GoogleMapType,
  key: string,
  fetchImpl: FetchLike,
  opts?: { referer?: string },
): Promise<GoogleSession> {
  const body: Record<string, unknown> = { mapType, language: 'en', region: 'SO' };
  // Satellite carries road labels via layerRoadmap (observed to render), so a
  // satellite view is never a label-less photo the user cannot navigate.
  if (mapType === 'satellite') body.layerTypes = ['layerRoadmap'];
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  // Node-side probes/tests set Referer to satisfy the dev key's referrer
  // restriction; a browser sets it automatically and must not have it forced.
  if (opts?.referer) headers.Referer = opts.referer;

  const res = await fetchImpl(`${TILES_HOST}/v1/createSession?key=${key}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`createSession ${mapType} → ${res.status}`);
  const j = (await res.json()) as Record<string, unknown>;
  if (typeof j.session !== 'string' || typeof j.expiry === 'undefined') {
    throw new Error(`createSession ${mapType} → malformed body`);
  }
  return {
    mapType,
    session: j.session,
    expirySec: Number(j.expiry),
    tileWidth: Number(j.tileWidth) || 256,
    tileHeight: Number(j.tileHeight) || 256,
    imageFormat: String(j.imageFormat || (mapType === 'satellite' ? 'jpeg' : 'png')),
  };
}

/** True when a cached session is within the refresh margin of expiry (or past
 *  it). Pure — `nowSec` is passed so tests pin time. */
export function sessionNeedsRefresh(session: GoogleSession, nowSec: number): boolean {
  return nowSec > session.expirySec - SESSION_REFRESH_MARGIN_SEC;
}

/**
 * A per-mapType session cache shared across an app's surfaces. It de-dupes
 * concurrent creates (many maps mount at once) and refreshes a session only when
 * it is within a day of expiry. The cache holds no key — the key is passed to
 * each call, so the cache object can be a module singleton without ever storing a
 * secret.
 */
export interface SessionCache {
  /** Return a live session for `mapType`, creating or refreshing as needed. */
  get(mapType: GoogleMapType, key: string, opts?: { referer?: string }): Promise<GoogleSession>;
  /** Drop the cached session for a mapType (used by tests / a hard reset). */
  invalidate(mapType?: GoogleMapType): void;
}

/**
 * Optional durable backing for the session cache (slice 56f). Sessions last ~2
 * weeks, but the in-memory cache dies with the page — so an installed PWA that
 * Android killed paid a cold createSession on its next map open or first basemap
 * switch ("the first time after a while"). A surface may pass a store (the web app
 * uses localStorage) so a still-valid session survives the restart. The store holds
 * a session TOKEN only — never imagery, never the key — and every failure is the
 * caller's to swallow: `read` returns null, `write` may no-op.
 */
export interface SessionStore {
  read(mapType: GoogleMapType): GoogleSession | null;
  write(session: GoogleSession): void;
}

export function createSessionCache(
  fetchImpl: FetchLike,
  nowSec: () => number = () => Math.floor(Date.now() / 1000),
  store?: SessionStore,
): SessionCache {
  const sessions = new Map<GoogleMapType, GoogleSession>();
  const inflight = new Map<GoogleMapType, Promise<GoogleSession>>();

  async function fresh(mapType: GoogleMapType, key: string, opts?: { referer?: string }) {
    const s = await createSession(mapType, key, fetchImpl, opts);
    sessions.set(mapType, s);
    store?.write(s);
    return s;
  }

  return {
    async get(mapType, key, opts) {
      const cached = sessions.get(mapType);
      if (cached && !sessionNeedsRefresh(cached, nowSec())) return cached;
      if (!cached && store) {
        const stored = store.read(mapType);
        if (stored && stored.mapType === mapType && !sessionNeedsRefresh(stored, nowSec())) {
          sessions.set(mapType, stored);
          return stored;
        }
      }
      const existing = inflight.get(mapType);
      if (existing) return existing;
      const p = fresh(mapType, key, opts).finally(() => inflight.delete(mapType));
      inflight.set(mapType, p);
      return p;
    },
    invalidate(mapType) {
      if (mapType) sessions.delete(mapType);
      else sessions.clear();
    },
  };
}

/** The MapLibre raster tile URL template for a session. maplibre substitutes
 *  {z}/{x}/{y}. The key rides in the query string exactly as the tile GETs the
 *  probe made (200 image/*). */
export function tileUrlTemplate(session: GoogleSession, key: string): string {
  return `${TILES_HOST}/v1/2dtiles/{z}/{x}/{y}?session=${session.session}&key=${key}`;
}

/** Fetch the viewport copyright + max-zoom info for the current view. Returns
 *  null on any failure — attribution degrades to the static logo, never throws
 *  into a moveend handler. */
export async function fetchViewportCopyright(
  session: GoogleSession,
  key: string,
  bounds: { north: number; south: number; east: number; west: number; zoom: number },
  fetchImpl: FetchLike,
  opts?: { referer?: string },
): Promise<ViewportInfo | null> {
  try {
    const q =
      `zoom=${bounds.zoom}&north=${bounds.north}&south=${bounds.south}` +
      `&east=${bounds.east}&west=${bounds.west}`;
    const headers: Record<string, string> = {};
    if (opts?.referer) headers.Referer = opts.referer;
    const res = await fetchImpl(
      `${TILES_HOST}/tile/v1/viewport?session=${session.session}&key=${key}&${q}`,
      { headers },
    );
    if (!res.ok) return null;
    const j = (await res.json()) as Record<string, unknown>;
    return {
      copyright: String(j.copyright ?? ''),
      maxZoomRects: Array.isArray(j.maxZoomRects) ? j.maxZoomRects.length : 0,
    };
  } catch {
    return null;
  }
}

// ── The pure fallback state machine ──────────────────────────────────────────
//
// One reducer decides which basemap is shown and whether the Google options are
// offered at all. It is a PURE function of (state, action): no timers, no fetch,
// no localStorage — the adapter owns those and feeds observations in. This is
// what makes "a 403 flips but a lone 404 does not" a unit test with a mutation
// control, rather than something only a live network could exercise.

export interface BasemapState {
  /** The basemap currently shown. */
  choice: BasemapChoice;
  /**
   * Google options are LATCHED OFF for the rest of this page-session. Once a
   * session create fails or the Google source errors past threshold, we stop
   * offering Google until a reload — never a retry loop on a metered link.
   */
  latched: boolean;
  /** Whether to show the non-blocking "Google unavailable — street map" notice.
   *  Set on an involuntary fallback; cleared by the user dismissing it or by a
   *  deliberate re-select. */
  notice: boolean;
  /** Timestamps (ms) of recent non-403/429 tile errors, inside the flip window.
   *  Internal to the reducer; the adapter passes `now` on each error action. */
  errorWindow: number[];
}

export type BasemapAction =
  | { type: 'init'; persisted: string | null; hasKey: boolean }
  | { type: 'select'; choice: BasemapChoice }
  | { type: 'sessionFailure' }
  | { type: 'tileError'; status: number; now: number }
  | { type: 'dismissNotice' };

/** Which choices a switcher may offer. Google is HIDDEN (not disabled) when there
 *  is no key or when we have latched off — "absence over disabled" (CLAUDE.md).
 *  Street map is always offered. */
export function availableChoices(hasKey: boolean, latched: boolean): BasemapChoice[] {
  if (!hasKey || latched) return ['openfreemap'];
  return ['google-roadmap', 'google-satellite', 'openfreemap'];
}

function isGoogle(choice: BasemapChoice): boolean {
  return choice === 'google-roadmap' || choice === 'google-satellite';
}

/** Resolve the default choice: a valid persisted value wins; else Google Map when
 *  a key is present and we have not latched off; else the street map. A persisted
 *  Google choice is honoured ONLY if Google is still available — a latched-off or
 *  key-less load of a persisted `google-*` falls to the street map. */
export function resolveDefault(persisted: string | null, hasKey: boolean, latched: boolean): BasemapChoice {
  const valid: BasemapChoice[] = ['google-roadmap', 'google-satellite', 'openfreemap'];
  const p = valid.includes(persisted as BasemapChoice) ? (persisted as BasemapChoice) : null;
  const googleOk = hasKey && !latched;
  if (p) {
    if (isGoogle(p) && !googleOk) return 'openfreemap';
    return p;
  }
  return googleOk ? 'google-roadmap' : 'openfreemap';
}

export function initialBasemapState(): BasemapState {
  return { choice: 'openfreemap', latched: false, notice: false, errorWindow: [] };
}

export function basemapReducer(state: BasemapState, action: BasemapAction): BasemapState {
  switch (action.type) {
    case 'init': {
      const choice = resolveDefault(action.persisted, action.hasKey, state.latched);
      return { ...state, choice, notice: false, errorWindow: [] };
    }
    case 'select': {
      // A user-chosen Google option is ignored if we have latched off (the
      // switcher would not offer it, but the reducer stays honest). A deliberate
      // choice always clears the fallback notice and the error window.
      if (isGoogle(action.choice) && state.latched) {
        return { ...state, choice: 'openfreemap', notice: false, errorWindow: [] };
      }
      return { ...state, choice: action.choice, notice: false, errorWindow: [] };
    }
    case 'sessionFailure': {
      // createSession failed → latch Google off, show the notice, fall to street.
      return { ...state, choice: 'openfreemap', latched: true, notice: true, errorWindow: [] };
    }
    case 'tileError': {
      // A 403 or 429 is authoritative (referrer/quota/billing) — flip immediately.
      if (action.status === 403 || action.status === 429) {
        return { ...state, choice: 'openfreemap', latched: true, notice: true, errorWindow: [] };
      }
      // Any other error status contributes to the window. Over-zoom is a 200
      // (observed) so it never reaches here; a lone transient error does not
      // flip. Prune the window to the last ERROR_FLIP_WINDOW_MS, then decide.
      const win = [...state.errorWindow, action.now].filter((t) => action.now - t < ERROR_FLIP_WINDOW_MS);
      if (win.length >= ERROR_FLIP_THRESHOLD) {
        return { ...state, choice: 'openfreemap', latched: true, notice: true, errorWindow: [] };
      }
      return { ...state, errorWindow: win };
    }
    case 'dismissNotice':
      return { ...state, notice: false };
    default:
      return state;
  }
}

/** Whether a choice is one of the Google basemaps (for adapters/components). */
export function isGoogleChoice(choice: BasemapChoice): boolean {
  return isGoogle(choice);
}

/** The Google session mapType a choice needs, or null for the street map. */
export function mapTypeForChoice(choice: BasemapChoice): GoogleMapType | null {
  if (choice === 'google-roadmap') return 'roadmap';
  if (choice === 'google-satellite') return 'satellite';
  return null;
}
